import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    setPagination,
    setSort,
    searchSuppliers,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/supplier/supplierActions';
import SupplierForm from '../../components/pageModule/Suppliers/SupplierForm';
import CloudActionButtons, { isContactCloudLocked } from '../../components/pageModule/Customers/CloudActionButtons';
import { useHasPermission } from '../../utils/permissionUtils';
import supplierService from '../../services/supplierService';

const MySwal = withReactContent(Swal);

const Suppliers = ({ 
    excludedActions = [], 
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = []
}) => {

    const hasCreatePermission = useHasPermission(['create suppliers']);
    const hasUpdatePermission = useHasPermission(['update suppliers']);
    const hasViewPermission = useHasPermission(['view suppliers']);

    const supplierState = useSelector(state => state.supplier);
    const dispatch = useDispatch();
    const [cloudLoadingBySupplier, setCloudLoadingBySupplier] = useState({});

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createSupplier(formData));
            toast.success('Supplier created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating supplier', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateSupplier(id, formData));
            toast.success('Supplier updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating supplier', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteSupplier(id));
                toast.success('Supplier deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting supplier', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleCloudPush = useCallback(async (row) => {
        if (!row?.id) {
            return;
        }

        if (isContactCloudLocked(row?.cloud)) {
            toast.warning('Supplier cloud sync is locked', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return;
        }

        setCloudLoadingBySupplier(prev => ({ ...prev, [row.id]: true }));
        try {
            await supplierService.pushCloud(row.id);
            toast.success('Supplier queued for cloud sync', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            await dispatch(fetchSuppliers({}, {}, true));
        } catch (error) {
            toast.error(error.message || 'Error pushing supplier to cloud', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setCloudLoadingBySupplier(prev => ({ ...prev, [row.id]: false }));
        }
    }, [dispatch]);

    const columns = useMemo(() => [
        {
            id: 'supplier_code',
            name: 'Supplier Code',
            selector: row => row.supplier_code,
            grow: 2,
        },
        {
            id: 'name',
            name: 'Name',
            selector: row => row.name?.en ?? '-',
            sortable: true,
            sortField: 'suppliers.name',
            minWidth: '200px',
            wrap: true,
        },
        {
            id: 'email',
            name: 'Email',
            selector: row => row.email,
            sortField: 'contact_email',
            minWidth: '200px',
            wrap: true,
        },
        {
            id: 'phone',
            name: 'Phone',
            selector: row => row.phone_number,
        },
        {
            id: 'vat_group',
            name: 'VAT Group',
            selector: row => row.vat_group?.label ?? '-',
            sortable: true,
            sortField: 'suppliers.vat_group_id',
            grow: 2,
        },
        {
            id: 'status',
            name: 'Status',
            selector: row => row.is_active ?
                <span className="badge bg-success">Active</span> :
                <span className="badge bg-danger">Inactive</span>,
        },
        // {
        //     id: 'cloud',
        //     name: 'Cloud',
        //     minWidth: '70px',
        //     cell: (row) => (
        //         <CloudActionButtons
        //             row={row}
        //             onPush={handleCloudPush}
        //             isPushing={!!cloudLoadingBySupplier[row?.id]}
        //         />
        //     ),
        //     ignoreRowClick: true,
        //     allowOverflow: true,
        // },
    ], [handleCloudPush, cloudLoadingBySupplier]);


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter) => dispatch(fetchSuppliers(queryFilter, activeFilter, shouldUseFilter)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchSuppliers(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    return (
        <>
            <GenericPage
                title="Suppliers"
                moduleName="Supplier"
                moduleKey="suppliers"
                state={supplierState}
                actions={actions}
                FormComponent={SupplierForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}
                sidebarClassName="right-sidebar-80"
                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Suppliers;
